import { Route, Navigate, createRoutesFromElements, useParams } from 'react-router-dom'
import { AppShell } from './AppShell'
import { Home } from '../pages/Home'
import { Agents } from '../pages/Agents'
import { AgentDetail } from '../pages/AgentDetail'
import { GlobalSettings, firstGlobalSettingsTabId, globalSettingsTabPath } from '../pages/GlobalSettings'
import { AuditLog } from '../pages/AuditLog'
import { MambuMcp } from '../pages/MambuMcp'
import { MambuMcpDetail } from '../pages/MambuMcpDetail'
import { McpAnalytics } from '../pages/McpAnalytics'
import { McpAnalyticsDetail, MCP_ANALYTICS_DEFAULT_TAB } from '../pages/McpAnalyticsDetail'
import { Marketplace, Workflows, Analytics } from '../pages/PreviewPage'
import { OAuthCallback } from '../pages/OAuthCallback'
import { AccessDenied } from '../pages/AccessDenied'
import { RequireAgentsContract } from './RequireAgentsContract'
import { RequireEntitlements } from './RequireEntitlements'
import { RequireMcpAccess } from './RequireMcpAccess'
import { RequireSectionAccess } from './RequireSectionAccess'

/** `/agents/:id` (no tab) — redirects to the first tab (T-URL-05), keeping
 * old tab-less deep links working. */
function AgentDetailIndexRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/agents/${id}/agent-dna`} replace />
}

/** `/global-settings` (no tab) — redirects to the first tab (T-URL-05). */
function GlobalSettingsIndexRedirect() {
  return <Navigate to={globalSettingsTabPath(firstGlobalSettingsTabId())} replace />
}

/** `/mcp/:product` (no tab) — redirects to the first tab (T-URL-05). */
function MambuMcpDetailIndexRedirect() {
  const { product } = useParams<{ product: string }>()
  return <Navigate to={`/mcp/${product}/tenant`} replace />
}

/** `/mcp-analytics/:product` (no tab) — redirects to the first tab (T-URL-05). */
function McpAnalyticsDetailIndexRedirect() {
  const { product } = useParams<{ product: string }>()
  return <Navigate to={`/mcp-analytics/${product}/${MCP_ANALYTICS_DEFAULT_TAB}`} replace />
}

export const appRoutes = createRoutesFromElements(
  <>
    {/* Auth routes — outside the AppShell guard so they render without a JWT */}
    <Route path="/oauth2/callback" element={<OAuthCallback />} />
    <Route path="/access-denied" element={<AccessDenied />} />

    <Route element={<AppShell />}>
      {/* Legacy root — redirects to /home (MS-740 R-NAV-8) */}
      <Route path="/" element={<Navigate to="/home" replace />} />
      <Route path="/home" element={<Home />} />
      {/* MS-866 AC6 — a failed entitlements lookup redirects every route below
          this layout (i.e. everything except /home) back to /home, which shows
          the degraded-state banner in place of its cards. */}
      <Route element={<RequireEntitlements />}>
        <Route path="/agents" element={<RequireSectionAccess section="agents"><Agents /></RequireSectionAccess>} />
        <Route path="/agents/:id" element={<RequireSectionAccess section="agents"><RequireAgentsContract><AgentDetailIndexRedirect /></RequireAgentsContract></RequireSectionAccess>} />
        <Route path="/agents/:id/:tab" element={<RequireSectionAccess section="agents"><RequireAgentsContract><AgentDetail /></RequireAgentsContract></RequireSectionAccess>} />
        {/* Legacy redirects — remove after one release */}
        <Route path="/sparks" element={<Navigate to="/agents" replace />} />
        <Route path="/sparks/:id" element={<Navigate to="/agents" replace />} />
        <Route path="/mcp" element={<RequireMcpAccess><MambuMcp /></RequireMcpAccess>} />
        <Route path="/mcp/:product" element={<RequireMcpAccess><MambuMcpDetailIndexRedirect /></RequireMcpAccess>} />
        <Route path="/mcp/:product/:tab" element={<RequireMcpAccess><MambuMcpDetail /></RequireMcpAccess>} />
        {/* Legacy redirects — remove after one release */}
        <Route path="/mambu-mcp" element={<Navigate to="/mcp" replace />} />
        <Route path="/mambu-mcp/core" element={<Navigate to="/mcp/core/tenant" replace />} />
        {/* MCP Analytics (MS-700). The active tab is a path segment (`/:product/:tab`),
            so a reload lands on it (AC-6); see McpAnalyticsDetail. */}
        <Route path="/mcp-analytics" element={<RequireMcpAccess><McpAnalytics /></RequireMcpAccess>} />
        <Route path="/mcp-analytics/:product" element={<RequireMcpAccess><McpAnalyticsDetailIndexRedirect /></RequireMcpAccess>} />
        <Route path="/mcp-analytics/:product/:tab" element={<RequireMcpAccess><McpAnalyticsDetail /></RequireMcpAccess>} />
        <Route path="/global-settings" element={<RequireSectionAccess section="global"><GlobalSettingsIndexRedirect /></RequireSectionAccess>} />
        <Route path="/global-settings/:tab" element={<RequireSectionAccess section="global"><GlobalSettings /></RequireSectionAccess>} />
        <Route path="/audit-log" element={<RequireSectionAccess section="audit"><AuditLog /></RequireSectionAccess>} />
        <Route path="/marketplace" element={<RequireSectionAccess section="marketplace"><Marketplace /></RequireSectionAccess>} />
        <Route path="/workflows" element={<RequireSectionAccess section="workflows"><Workflows /></RequireSectionAccess>} />
        <Route path="/agents-analytics" element={<RequireSectionAccess section="analytics"><Analytics /></RequireSectionAccess>} />
        {/* Legacy redirect — remove after one release */}
        <Route path="/analytics" element={<Navigate to="/agents-analytics" replace />} />
      </Route>
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Route>
  </>
)
